import { AuthenticatedUser } from "authentication/domain/AuthenticatedUser";
import { FullUser } from "authentication/domain/FullUser";
import { IResponseUser } from "authentication/domain/IResponseUser";
import { IUser } from "authentication/domain/IUser";
import API from "utils/API";
import { LocalStorage } from "utils/local-storage";
import { IResponseGroups } from "authentication/domain/IResponseGroups";
import { IResponseUsers } from "authentication/domain/IResponseUsers";
import { IUserGroup } from "authentication/domain/IUserGroup";
import authHeader from "utils/auth-header";
import { IUserUpdate } from "authentication/domain/IUserUpdate";
import { IResponseMessage } from "authentication/domain/IResponseMessage";
import { IUserCreate } from "authentication/domain/IUserCreate";
import { IMessage } from "domain/IMessage";
import { IGetAllSessionsResponse } from "authentication/domain/IGetAllSessionsResponse";
import { ISession } from "authentication/domain/ISession";

const AUTH_PATH = "/authentication/public/desktop/me";

const login = async (username: string, accessToken: string) => {
  try {
    if (accessToken != null) {
      let authenticatedUser = new AuthenticatedUser(username, accessToken);
      LocalStorage.setItem(
        "authenticatedUser",
        JSON.stringify(authenticatedUser)
      );
    }
  } catch (err) {
    throw err;
  }
};

const logout = (): void => {
  LocalStorage.removeItem("authenticatedUser");
  LocalStorage.removeItem("currentUser");
};

const getCurrentUser = async (): Promise<FullUser> => {
  try {
    var headers = authHeader();
    const response = await API.get<IResponseUser>(AUTH_PATH, {
      headers,
    });
    let user = response.data.user;
    return new FullUser(
      user.name,
      user.username,
      user.email,
      user.userGroups,
      user.allowedModules,
      user.userGroupNames,
      user.employee
    );
  } catch (err) {
    throw err;
  }
};

const getCurrentlyActiveSessions = async (): Promise<ISession[]> => {
  var headers = authHeader();
  try {
    const response = await API.get<IGetAllSessionsResponse>(
      "authentication/public/active-sessions",
      { headers }
    );
    return response.data.sessions;
  } catch (err) {
    throw err;
  }
};

const getUsers = async (): Promise<IUser[]> => {
  var headers = authHeader();
  try {
    const response = await API.get<IResponseUsers>(
      "/authentication/public/user",
      { headers }
    );
    let users = response.data.users;
    return users;
  } catch (err) {
    throw err;
  }
};

const createUser = async (user: IUserCreate): Promise<string> => {
  var headers = authHeader();
  try {
    const response = await API.post<string>(
      "/authentication/protected/user",
      user,
      { headers }
    );
    let message = response.data;
    return message;
  } catch (err) {
    throw err;
  }
};

const updateUser = async (
  user: IUserUpdate,
  username: string
): Promise<IUser> => {
  var headers = authHeader();
  try {
    const response = await API.put<IResponseUser>(
      `/authentication/protected/user/${username}/change-name-and-employee`,
      user,
      { headers }
    );
    let users = response.data.user;
    return users;
  } catch (err) {
    throw err;
  }
};

const deleteUser = async (username: string): Promise<string> => {
  var headers = authHeader();
  try {
    const response = await API.delete<IResponseMessage>(
      `/authentication/protected/user/${username}`,
      { headers }
    );
    return response.data.message;
  } catch (err) {
    throw err;
  }
};

const updateCurrentUsername = async (user: IUserUpdate): Promise<IUser> => {
  var headers = authHeader();
  try {
    console.log(user);
    const response = await API.put<IResponseUser>(
      "/authentication/protected/user/me/change-name-and-employee",
      { ...user },
      { headers }
    );
    let updatedUser = response.data.user;
    return updatedUser;
  } catch (err) {
    throw err;
  }
};

const updateCurrentEmail = async (data: { email: string }): Promise<IUser> => {
  var headers = authHeader();
  try {
    const response = await API.put<IResponseUser>(
      "/authentication/protected/user/me/change-email",
      { ...data },
      { headers }
    );
    let user = response.data.user;
    return user;
  } catch (err) {
    throw err;
  }
};

const getRoles = async (): Promise<IUserGroup[]> => {
  var headers = authHeader();
  try {
    const response = await API.get<IResponseGroups>(
      "/authentication/public/group",
      { headers }
    );
    let roles = response.data.userGroups;
    return roles;
  } catch (err) {
    throw err;
  }
};

const joinGroups = async (
  username: string,
  groups: string[]
): Promise<IUser> => {
  var headers = authHeader();
  try {
    const response = await API.put<IResponseUser>(
      `/authentication/protected/user/${username}/join-groups`,
      { groups },
      { headers }
    );
    let user = response.data.user;
    return user;
  } catch (err) {
    throw err;
  }
};

const leaveGroups = async (
  username: string,
  groups: string[]
): Promise<IUser> => {
  var headers = authHeader();
  try {
    const response = await API.put<IResponseUser>(
      `/authentication/protected/user/${username}/leave-groups`,
      { groups },
      { headers }
    );
    let user = response.data.user;
    return user;
  } catch (err) {
    throw err;
  }
};

const updatePassword = async (
  username: string,
  password: string
): Promise<IUser> => {
  var headers = authHeader();
  try {
    const response = await API.put<IResponseUser>(
      `/authentication/protected/user/${username}/password`,
      { password },
      { headers }
    );
    let user = response.data.user;
    return user;
  } catch (err) {
    throw err;
  }
};

const updateCurrentUserPassword = async (
  password: string,
  passwordConfirmation: string): Promise<IUser> => {
  var headers = authHeader();
  try {
    const response = await API.put<IResponseUser>(
      "/authentication/public/me/password",
      { password, passwordConfirmation },
      { headers }
    );
    let user = response.data.user;
    return user;
  } catch (err) {
    throw err;
  }
};

const getProfilePictureForSpecificUser = async (username: string): Promise<string | null> => {
  var headers = {
    ...authHeader(),
  };
  try {
    const response = await API.get<any>(
      `/authentication/protected/user/${username}/profile-picture`,
      { headers, responseType: "arraybuffer" }
    );
    if (response.data.byteLength === 0) return null;
    let image = btoa(
      new Uint8Array(response.data).reduce(
        (data, byte) => data + String.fromCharCode(byte),
        ""
      )
    );
    return `data:${response.headers[
      "content-type"
    ]?.toLowerCase()};base64,${image}`;
  } catch (err) {
    console.log(err);
    throw err;
  }
};

const uploadProfilePictureForSpecificUser = async (
  image: any,
  username: string
): Promise<IMessage> => {
  const data = new FormData();
  data.append("profileImage", image);
  var headers = {
    "Content-Type": "multipart/form-data",
    ...authHeader(),
  };
  try {
    const response = await API.post<IMessage>(
      `/authentication/protected/user/${username}/profile-picture`,
      data,
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const getMyProfilePicture = async (): Promise<string | null> => {
  var headers = {
    ...authHeader(),
  };
  try {
    const response = await API.get<any>(
      "/authentication/public/my-profile-picture",
      { headers, responseType: "arraybuffer" }
    );
    if (response.data.byteLength === 0) return null;
    let image = btoa(
      new Uint8Array(response.data).reduce(
        (data, byte) => data + String.fromCharCode(byte),
        ""
      )
    );
    return `data:${response.headers[
      "content-type"
    ]?.toLowerCase()};base64,${image}`;
  } catch (err) {
    console.log(err);
    throw err;
  }
};

const uploadMyProflePicture = async (image: any): Promise<IMessage> => {
  var headers = {
    "Content-Type": "multipart/form-data",
    ...authHeader(),
  };
  const data = new FormData();
  data.append("profileImage", image);
  try {
    const response = await API.post<IMessage>(
      "/authentication/public/my-profile-picture",
      data,
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const setEmployeeForUser = async (
  username: string, employeeId: number
): Promise<IMessage> => {
  var headers = authHeader();
  try {
    const response = await API.put<IMessage>(
      `/authentication/protected/user/${username}/employee`,
      { employeeId },
      { headers }
    );
    let message = response.data;
    return message;
  } catch (err) {
    throw err;
  }
};

export const authServices = {
  login,
  logout,
  getCurrentUser,
  getCurrentlyActiveSessions,
  createUser,
  getUsers,
  updateUser,
  updateCurrentUsername,
  updateCurrentEmail,
  deleteUser,
  getRoles,
  joinGroups,
  leaveGroups,
  updatePassword,
  updateCurrentUserPassword,
  getProfilePictureForSpecificUser,
  uploadProfilePictureForSpecificUser,
  getMyProfilePicture,
  uploadMyProflePicture,
  setEmployeeForUser
};
